import { FirestoreDataConverter } from '@angular/fire/firestore';
import { GimOrderState } from '../enums/gim-order-state.enum';

export interface GimVisual {
  name: string;
  plaisirQuantity: number;
  stPriestQuantity: number;
  otherQuantity: number;
}

export interface GimEstimate {
  /** GIM format (FORMAT_2...) */
  displayArea: string;
  /** GIM category (CAISSON, COLLE) */
  displayCategory: string;
  printType: string;
  sideType: string;
  price: number;
  visuals: GimVisual[];
}

export interface GimQuotationOption {
  name: string;
  price: number;
  comment: string;
}

/**
 * GIM quotation synchronized from JCDecaux, created in Multipress on import
 */
export class GimOrder {
  /**
   * Must stay above the 540s timeout of the GIM push functions, and equal to functions PUSH_LOCK_MS.
   */
  static readonly PUSH_LOCK_MS = 10 * 60 * 1000;

  /** Firestore ID */
  id: string;
  organizationId: string;
  /** GIM quotation reference */
  reference: number;
  purchaseOrderNumber: string;
  /** GIM quotation status */
  status: string;
  comment: string;
  campaignCode: string;
  campaignName: string;
  campaignType: string;
  sponsor: string;
  salesTeam: string;
  recordReference: number;
  displayWeek: number;
  displayYear: number;
  /** Delivery date (YYYY-MM-DD) */
  deliveryDate: string;
  submittingItemsDate: string;
  estimates: GimEstimate[];
  options: GimQuotationOption[];
  /** Import date */
  date: Date;
  /** À traiter, Traitée once created in Multipress, or Archivée */
  state: GimOrderState;
  /** A push to Multipress is running */
  pushIsRunning: boolean;
  /**
   * When the current push started.
   * A lock older than {@link GimOrder.PUSH_LOCK_MS} is abandoned (same value as functions PUSH_LOCK_MS).
   */
  pushStartedAt: Date | null;
  /** Technical message of the last push error */
  error: string;
  /** Code of the last push error, described by gim.error.<code> */
  errorCode: string;
  /** Values inserted in the error description */
  errorParams: Record<string, string | number>;
  /** Multipress job number */
  mpTransactionId: string;
  pushedDate: Date | null;
  /** Okto order created outside of the module whose name mentions the GIM number, empty when none */
  existingOrderId: string;
  existingOrderName: string;

  constructor(data?: Partial<GimOrder>) {
    this.id = data?.id ?? '';
    this.organizationId = data?.organizationId ?? '';
    this.reference = data?.reference ?? 0;
    this.purchaseOrderNumber = data?.purchaseOrderNumber ?? '';
    this.status = data?.status ?? '';
    this.comment = data?.comment ?? '';
    this.campaignCode = data?.campaignCode ?? '';
    this.campaignName = data?.campaignName ?? '';
    this.campaignType = data?.campaignType ?? '';
    this.sponsor = data?.sponsor ?? '';
    this.salesTeam = data?.salesTeam ?? '';
    this.recordReference = data?.recordReference ?? 0;
    this.displayWeek = data?.displayWeek ?? 0;
    this.displayYear = data?.displayYear ?? 0;
    this.deliveryDate = data?.deliveryDate ?? '';
    this.submittingItemsDate = data?.submittingItemsDate ?? '';
    this.estimates = data?.estimates ?? [];
    this.options = data?.options ?? [];
    this.date = data?.date ?? new Date();
    this.state = data?.state ?? GimOrderState.TO_PROCESS;
    this.pushIsRunning = data?.pushIsRunning ?? false;
    this.pushStartedAt = data?.pushStartedAt ?? null;
    this.error = data?.error ?? '';
    this.errorCode = data?.errorCode ?? '';
    this.errorParams = data?.errorParams ?? {};
    this.mpTransactionId = data?.mpTransactionId ?? '';
    this.pushedDate = data?.pushedDate ?? null;
    this.existingOrderId = data?.existingOrderId ?? '';
    this.existingOrderName = data?.existingOrderName ?? '';
  }

  /**
   * Number the campaign name starts with, e.g. 233190 for "233190 - NG PROD NOTORIETE 2026".
   * This is the GIM code used in the Multipress job name.
   * @returns The code, empty when the campaign name has none
   */
  get campaignNumber(): string {
    return /^\s*(\d{5,7})\b/.exec(this.campaignName ?? '')?.[1] ?? '';
  }

  /**
   * Campaign name without its GIM code: "NG PROD NOTORIETE 2026" for "233190 - NG PROD NOTORIETE 2026".
   * @returns The name, or the original text when it does not start with a code
   */
  get campaignLabel(): string {
    const label = (this.campaignName ?? '').replace(/^\s*\d{5,7}\b\s*(-\s*)?/, '').trim();
    return label || this.campaignName;
  }

  /**
   * Campaign name without its GIM code, for a column next to the GIM code: "NG PROD NOTORIETE 2026" for
   * "233190 - NG PROD NOTORIETE 2026", empty when the campaign name is only the code (it would show it twice)
   * @returns The name, empty when the campaign name has nothing but its code
   */
  get campaignTitle(): string {
    return (this.campaignName ?? '').replace(/^\s*\d{5,7}\b\s*(-\s*)?/, '').trim();
  }

  /**
   * Total number of posters of the order
   * @returns The quantity
   */
  get quantity(): number {
    return this.estimates.reduce((total, estimate) => total + estimate.visuals.reduce((sum, visual) =>
      sum + visual.plaisirQuantity + visual.stPriestQuantity + visual.otherQuantity, 0), 0);
  }

  /**
   * Number of posters without depot: their delivery addresses are not created, they are set by hand in Multipress
   * @returns The quantity
   */
  get otherQuantity(): number {
    return this.estimates.reduce((total, estimate) =>
      total + estimate.visuals.reduce((sum, visual) => sum + visual.otherQuantity, 0), 0);
  }

  /**
   * A push the console should still show as running.
   * A lock without a start date, or older than the function timeout, is abandoned so the order can be retried.
   * @returns true while a live push holds the lock
   */
  get isPushing(): boolean {
    return this.pushIsRunning && !GimOrder.isPushLockStale(this.pushStartedAt);
  }

  /**
   * The order was created in Multipress
   * @returns true when the state is Traitée
   */
  get isProcessed(): boolean {
    return this.state === GimOrderState.PROCESSED;
  }

  /**
   * The order can still be created in Multipress or archived
   * @returns true while the state is À traiter
   */
  get isToProcess(): boolean {
    return this.state === GimOrderState.TO_PROCESS;
  }

  /**
   * The order was archived and can be put back to À traiter
   * @returns true when the state is Archivée
   */
  get isArchived(): boolean {
    return this.state === GimOrderState.ARCHIVED;
  }

  /**
   * The order could not be created in Multipress
   * @returns true when the last push failed
   */
  get inError(): boolean {
    return this.state === GimOrderState.TO_PROCESS && !this.isPushing && (!!this.errorCode || !!this.error);
  }

  /**
   * A running push whose function can no longer be alive.
   * No start date counts as stale: the lock was left by a version that did not record it.
   * @param pushStartedAt When the push started
   * @param now Current time in epoch milliseconds
   * @returns true when the order can be retried
   */
  static isPushLockStale(pushStartedAt: Date | null, now = Date.now()): boolean {
    if (!pushStartedAt) {
      return true;
    }
    return now - pushStartedAt.getTime() >= GimOrder.PUSH_LOCK_MS;
  }

  /**
   * The orders are only written by the Cloud Functions
   */
  static converter: FirestoreDataConverter<GimOrder> = {
    toFirestore: () => ({}),
    fromFirestore: (snapshot, options) => {
      const data = snapshot.data(options);
      return new GimOrder({
        ...data,
        id: snapshot.id,
        date: data.date?.toDate ? data.date.toDate() : null,
        pushedDate: data.pushedDate?.toDate ? data.pushedDate.toDate() : null,
        pushStartedAt: data.pushStartedAt?.toDate ? data.pushStartedAt.toDate() : null,
      });
    }
  };
}
